import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { AppShell } from '@/components/shell/app-shell';
import { FullPageSpinner } from '@/components/shell/full-page-spinner';
import { useSession, type Permission } from '@/lib/session';
import { AccountDisabledPage } from '@/routes/account-disabled';
import { AssetByCodePage } from '@/routes/asset-by-code';
import { AssetDetailPage } from '@/routes/asset-detail';
import { AssetLabelsPage } from '@/routes/asset-labels';
import { AssetsPage } from '@/routes/assets';
import { AssignPage } from '@/routes/assign';
import { AuditPage } from '@/routes/audit';
import { DashboardPage } from '@/routes/dashboard';
import { EmployeeDetailPage } from '@/routes/employee-detail';
import { EmployeesPage } from '@/routes/employees';
import { ExitsPage } from '@/routes/exits';
import { ImportPage } from '@/routes/import';
import { LoginPage } from '@/routes/login';
import { MyAssetsPage } from '@/routes/my-assets';
import { NotFoundPage } from '@/routes/not-found';
import { NotOnboardedPage } from '@/routes/not-onboarded';
import { RepairsPage } from '@/routes/repairs';
import { ReportsPage } from '@/routes/reports';
import { RequestsPage } from '@/routes/requests';
import { ReturnPage } from '@/routes/return';
import { SettingsPage } from '@/routes/settings';

/**
 * Gate for every authenticated route.
 *
 * The three non-authenticated states are deliberately distinct destinations rather
 * than one "access denied": not signed in is a login problem, signed in with no
 * employee record is an HR onboarding problem, and exited is neither. They need
 * different words and different next steps.
 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const session = useSession();
  const location = useLocation();

  if (session.status === 'loading') return <FullPageSpinner />;
  // Carries where they were going, so signing in lands them there. Every reminder
  // email links straight at a queue, and without this each one arrived at the
  // dashboard for anyone not already signed in on that browser.
  if (session.status === 'unauthenticated') {
    return (
      <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />
    );
  }
  // Exited: credentials are valid, so /login would just loop them back here.
  if (session.status === 'disabled') return <Navigate to="/account-disabled" replace />;
  // Google says who they are; this system has never heard of them.
  if (session.status === 'not-onboarded') return <Navigate to="/not-onboarded" replace />;
  return <>{children}</>;
}

/**
 * Route-level permission gate.
 *
 * The API enforces this too; this only avoids a dead-end screen. Sends people to
 * their own assets rather than the dashboard, because a plain employee has no
 * permission to see the dashboard either and would otherwise bounce twice.
 */
function RequirePermission({
  permission,
  children,
}: {
  permission: Permission;
  children: React.ReactNode;
}) {
  const session = useSession();
  if (session.status !== 'authenticated') return <FullPageSpinner />;
  if (!session.user.permissions.includes(permission)) return <Navigate to="/my-assets" replace />;
  return <>{children}</>;
}

/**
 * Where "/" goes.
 *
 * An employee has no register to look at, so sending everyone to /dashboard would
 * bounce them straight out again. Landing them on their own assets is the only
 * screen that is theirs.
 */
function HomeRedirect() {
  const session = useSession();
  const from = (useLocation().state as { from?: string } | null)?.from;

  if (session.status !== 'authenticated') return <FullPageSpinner />;
  // Only a path this app put there itself. Checked anyway: a state that reached a
  // Navigate unread is an open redirect waiting for someone to notice it.
  if (from && from.startsWith('/') && !from.startsWith('//')) {
    return <Navigate to={from} replace />;
  }
  return session.user.permissions.includes('viewRegister') ? (
    <Navigate to="/dashboard" replace />
  ) : (
    <Navigate to="/my-assets" replace />
  );
}

export function App() {
  const session = useSession();

  return (
    <Routes>
      <Route
        path="/login"
        element={session.status === 'authenticated' ? <HomeRedirect /> : <LoginPage />}
      />
      <Route path="/not-onboarded" element={<NotOnboardedPage />} />
      <Route path="/account-disabled" element={<AccountDisabledPage />} />

      {/*
        Outside the app shell, and the only authenticated route that is.

        It exists to be printed. Inside the shell the sidebar and top bar have to be
        suppressed with print-only CSS, and one missed rule puts navigation chrome on a
        sheet of stickers. Still behind RequireAuth and manageAssets - a label sheet
        lists every asset code in the selection.
      */}
      <Route
        path="/assets/labels"
        element={
          <RequireAuth>
            <RequirePermission permission="manageAssets">
              <AssetLabelsPage />
            </RequirePermission>
          </RequireAuth>
        }
      />

      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route path="/" element={<HomeRedirect />} />

        {/* Every signed-in person, including a plain employee. */}
        <Route path="/my-assets" element={<MyAssetsPage />} />

        <Route
          path="/dashboard"
          element={
            <RequirePermission permission="viewRegister">
              <DashboardPage />
            </RequirePermission>
          }
        />
        <Route
          path="/assets"
          element={
            <RequirePermission permission="viewRegister">
              <AssetsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/assets/:assetId"
          element={
            <RequirePermission permission="viewRegister">
              <AssetDetailPage />
            </RequirePermission>
          }
        />
        {/* Where a scanned sticker lands. Short because it is typed by hand as often as
            it is scanned - see AssetByCodePage.

            Every signed-in person, deliberately: the sticker is on hardware an employee
            is holding, and a permission gate here bounced them to /my-assets without a
            word, which reads as the sticker being broken. The page decides what to show
            each audience - the register for IT, their own row for everyone else - and
            reaches for no data the viewer could not already read. */}
        <Route path="/a/:code" element={<AssetByCodePage />} />
        <Route
          path="/assign"
          element={
            <RequirePermission permission="assignAssets">
              <AssignPage />
            </RequirePermission>
          }
        />
        <Route
          path="/return"
          element={
            <RequirePermission permission="assignAssets">
              <ReturnPage />
            </RequirePermission>
          }
        />
        <Route
          path="/repairs"
          element={
            <RequirePermission permission="viewRegister">
              <RepairsPage />
            </RequirePermission>
          }
        />
        {/* The asset-request queue. viewRegister to read, manageAssets to review - the
            page checks the second itself, so HR and VIEWER can see what is outstanding
            without being able to decide it. The URL is in the reminder emails. */}
        <Route
          path="/requests"
          element={
            <RequirePermission permission="viewRegister">
              <RequestsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/employees"
          element={
            <RequirePermission permission="viewRegister">
              <EmployeesPage />
            </RequirePermission>
          }
        />
        <Route
          path="/employees/:employeeId"
          element={
            <RequirePermission permission="viewRegister">
              <EmployeeDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/exits"
          element={
            <RequirePermission permission="runExitClearance">
              <ExitsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/import"
          element={
            <RequirePermission permission="viewRegister">
              <ImportPage />
            </RequirePermission>
          }
        />
        <Route
          path="/reports"
          element={
            <RequirePermission permission="viewReports">
              <ReportsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/settings"
          element={
            <RequirePermission permission="manageSettings">
              <SettingsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/audit"
          element={
            <RequirePermission permission="manageSettings">
              <AuditPage />
            </RequirePermission>
          }
        />

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
